import React from "react";
import { Button, Card, Empty, List, Popconfirm, Spin, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { DrinkActivitySummary } from "../../../services/drinkService";
import { toAbvPercent } from "../../../services/drinks/utils/numbers";
import { useTranslation } from "react-i18next";
import { CardHeading, ListItemMeta } from "../../ListItemMeta";

const { Text } = Typography;

type RecentDrinksCardProps = {
    activities: DrinkActivitySummary[];
    selectedDay: Dayjs;
    loading: boolean;
    hasSelectedDayData: boolean;
    isOnline: boolean;
    onRefresh: () => void;
    onEdit: (activity: DrinkActivitySummary) => void;
    onDelete: (activity: DrinkActivitySummary) => void;
};

const RecentDrinksCard: React.FC<RecentDrinksCardProps> = ({
    activities,
    selectedDay,
    loading,
    hasSelectedDayData,
    isOnline,
    onRefresh,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation();

    return (
        <Card
            title={(
                <CardHeading>
                    {t("drinkStats.recentDrinks.title")}
                </CardHeading>
            )}
            extra={
                <Button onClick={onRefresh} loading={loading}>
                    {t("drinkStats.recentDrinks.refresh")}
                </Button>
            }
        >
            {loading && (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                    <Spin />
                </div>
            )}
            {!loading && hasSelectedDayData && (
                <List
                    dataSource={activities}
                    rowKey={(item) =>
                        item.activityId
                            ? String(item.activityId)
                            : `${item.drinkId ?? "unknown"}-${item.timestamp?.getTime() ?? 0}-${item.amountMl}`
                    }
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        responsive: true,
                        hideOnSinglePage: false,
                        itemRender: (_page, type, element) => type === "prev" || type === "next"
                            ? React.cloneElement(element as React.ReactElement<React.HTMLAttributes<HTMLElement>>, {
                                "aria-label": t(type === "prev" ? "common.previousPage" : "common.nextPage"),
                            })
                            : element,
                    }}
                    renderItem={(item) => {
                        const canModify = Boolean(item.activityId);
                        const durationMinutes = item.durationMinutes ??
                            (item.timestamp && item.startedAt
                                ? Math.max(
                                      0,
                                      Math.round(
                                          (item.timestamp.getTime() - item.startedAt.getTime()) / (60 * 1000)
                                      )
                                  )
                                : null);
                        let durationLabel: string | null = null;
                        if (durationMinutes === 0) {
                            durationLabel = t("drinkStats.recentDrinks.durationChugged");
                        } else if (durationMinutes !== null && item.startedAt) {
                            durationLabel = t("drinkStats.recentDrinks.durationWithStart", {
                                minutes: durationMinutes,
                                time: dayjs(item.startedAt).format("DD MMM YYYY HH:mm"),
                            });
                        } else if (durationMinutes !== null) {
                            durationLabel = t("drinkStats.recentDrinks.durationSimple", {
                                minutes: durationMinutes,
                            });
                        }
                        const itemActions = canModify
                            ? [
                                  <Button
                                      type="link"
                                      key="edit"
                                      onClick={() => {
                                          onEdit(item);
                                      }}
                                  >
                                      {t("drinkStats.recentDrinks.edit")}
                                  </Button>,
                                  <Popconfirm
                                      key="delete"
                                      title={t("drinkStats.recentDrinks.deleteConfirm")}
                                      okText={t("drinkStats.recentDrinks.deleteOk")}
                                      cancelText={t("drinkStats.recentDrinks.deleteCancel")}
                                      onConfirm={() => onDelete(item)}
                                  >
                                      <Button type="link" danger disabled={!isOnline} title={!isOnline ? t("drinkStats.recentDrinks.deleteOffline") : undefined}>
                                          {t("drinkStats.recentDrinks.delete")}
                                      </Button>
                                  </Popconfirm>,
                              ]
                            : [];

                        return (
                            <List.Item actions={itemActions}>
                                <ListItemMeta
                                    title={t("drinkStats.recentDrinks.entryTitle", {
                                        drink: item.drinkName,
                                        amount: item.amountMl,
                                    })}
                                >
                                    <div>
                                        {item.timestamp
                                            ? dayjs(item.timestamp).format("DD MMM YYYY HH:mm")
                                            : t("drinkStats.recentDrinks.noTimestamp")}
                                    </div>
                                    {durationLabel !== null && <div>{durationLabel}</div>}
                                </ListItemMeta>
                                <Text type="secondary">
                                    {item.promille
                                        ? t("drinkStats.recentDrinks.abvLabel", {
                                              abv: toAbvPercent(item.promille).toFixed(1),
                                          })
                                        : ""}
                                </Text>
                            </List.Item>
                        );
                    }}
                />
            )}
            {!loading && !hasSelectedDayData && (
                <Empty
                    description={t("drinkStats.recentDrinks.empty", {
                        date: selectedDay.format("DD MMM YYYY"),
                    })}
                />
            )}
        </Card>
    );
};

export default RecentDrinksCard;
